// Composant pour la gestion des demandes d'inscription en attente
import React, { useState, useEffect } from 'react';
import { Calendar, User, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

const PendingUsersManagement = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingUserId, setProcessingUserId] = useState(null);
  const [approvalData, setApprovalData] = useState({
    action: '',
    access_end_date: '',
    access_duration_days: '',
    rejection_reason: ''
  });

  // Charger les utilisateurs en attente
  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/users/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const users = await response.json();
        setPendingUsers(users);
      } else {
        const error = await response.json();
        toast.error(`Erreur: ${error.detail}`);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error);
      toast.error('Impossible de charger les demandes d\'inscription');
    } finally {
      setLoading(false);
    }
  };

  // Approuver ou rejeter un utilisateur
  const processUserRequest = async (userId, action, data) => {
    setProcessingUserId(userId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const requestBody = {
        action: action,
        ...data
      };
      
      const response = await fetch(`${backendUrl}/api/users/${userId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        
        // Recharger la liste
        await fetchPendingUsers();
        
        // Réinitialiser le formulaire
        setApprovalData({
          action: '',
          access_end_date: '',
          access_duration_days: '',
          rejection_reason: ''
        });
      } else {
        const error = await response.json();
        toast.error(`Erreur: ${error.detail}`);
      }
    } catch (error) {
      console.error('Erreur lors du traitement de la demande:', error);
      toast.error('Impossible de traiter la demande');
    } finally {
      setProcessingUserId(null);
    }
  };

  // Approuver avec date de fin ou durée
  const handleApprove = (userId) => {
    const { access_end_date, access_duration_days } = approvalData;
    
    if (!access_end_date && !access_duration_days) {
      toast.error('Veuillez définir une date de fin ou une durée d\'accès');
      return;
    }
    
    const approvalRequestData = {};
    
    if (access_end_date) {
      approvalRequestData.access_end_date = access_end_date;
    }
    
    if (access_duration_days) {
      approvalRequestData.access_duration_days = parseInt(access_duration_days);
    }
    
    processUserRequest(userId, 'approve', approvalRequestData);
  };

  // Rejeter avec raison
  const handleReject = (userId) => {
    const { rejection_reason } = approvalData;
    
    if (!rejection_reason.trim()) {
      toast.error('Veuillez indiquer une raison pour le rejet');
      return;
    }
    
    processUserRequest(userId, 'reject', { rejection_reason });
  };

  // Charger au montage du composant
  useEffect(() => {
    fetchPendingUsers();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Chargement des demandes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <User className="mr-2 h-6 w-6" />
          Demandes d'inscription en attente
        </h2>
        <button
          onClick={fetchPendingUsers}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
        >
          Actualiser
        </button>
      </div>
      
      {pendingUsers.length === 0 ? (
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-500">Aucune demande en attente</h3>
          <p className="text-gray-400">Toutes les demandes d'inscription ont été traitées.</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {pendingUsers.map((user) => (
            <div key={user.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex flex-col space-y-4">
                {/* Informations utilisateur */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{user.full_name}</h3>
                    <p className="text-gray-600">@{user.username}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <div className="flex items-center mt-2 text-sm text-gray-500">
                      <Clock className="h-4 w-4 mr-1" />
                      Demandé le {new Date(user.created_at).toLocaleDateString('fr-FR')}
                    </div>
                    <div className="mt-1">
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                        Nom d'utilisateur unique - Email partageable
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {user.role}
                    </span>
                  </div>
                </div>
                
                {/* Formulaire d'approbation */}
                <div className="border-t pt-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Section Approbation */}
                    <div>
                      <h4 className="font-medium text-green-700 mb-3">Approuver l'accès</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date de fin d'accès
                          </label>
                          <input
                            type="date"
                            value={approvalData.access_end_date}
                            onChange={(e) => setApprovalData(prev => ({
                              ...prev,
                              access_end_date: e.target.value
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                        </div>
                        
                        <div className="text-center text-sm text-gray-500">OU</div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Durée d'accès (jours)
                          </label>
                          <select
                            value={approvalData.access_duration_days}
                            onChange={(e) => setApprovalData(prev => ({
                              ...prev,
                              access_duration_days: e.target.value
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          >
                            <option value="">Sélectionner une durée</option>
                            <option value="30">30 jours (1 mois)</option>
                            <option value="90">90 jours (3 mois)</option>
                            <option value="180">180 jours (6 mois)</option>
                            <option value="365">365 jours (1 an)</option>
                            <option value="730">730 jours (2 ans)</option>
                          </select>
                        </div>
                        
                        <button
                          onClick={() => handleApprove(user.id)}
                          disabled={processingUserId === user.id}
                          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-lg flex items-center justify-center"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {processingUserId === user.id ? 'Traitement...' : 'Approuver'}
                        </button>
                      </div>
                    </div>
                    
                    {/* Section Rejet */}
                    <div>
                      <h4 className="font-medium text-red-700 mb-3">Rejeter la demande</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Raison du rejet
                          </label>
                          <textarea
                            value={approvalData.rejection_reason}
                            onChange={(e) => setApprovalData(prev => ({
                              ...prev,
                              rejection_reason: e.target.value
                            }))}
                            placeholder="Expliquez la raison du rejet..."
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          />
                        </div>
                        
                        <button
                          onClick={() => handleReject(user.id)}
                          disabled={processingUserId === user.id}
                          className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-lg flex items-center justify-center"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          {processingUserId === user.id ? 'Traitement...' : 'Rejeter'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingUsersManagement;